import React, { memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { formatLocalizedDateTime, formatSeriesMetaString, formatDuration } from '@jwp/ott-common/src/utils/formatting';
import { isLiveChannel, isSeries } from '@jwp/ott-common/src/utils/media';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import Lock from '@jwp/ott-theme/assets/icons/lock.svg?react';
import Today from '@jwp/ott-theme/assets/icons/today.svg?react';
import { testId } from '@jwp/ott-common/src/utils/common';
import type { PosterAspectRatio } from '@jwp/ott-common/src/utils/collection';
import { getLocalStorageValue } from '@jwp/ott-common/src/constants';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

import Image from '../Image/Image';
import Icon from '../Icon/Icon';

import styles from './Card.module.scss';

type ReplaceColon<T> = T extends `${infer Left}:${infer Right}` ? `${Left}${Right}` : T;
type PosterAspectRatioClass = ReplaceColon<PosterAspectRatio>;

type CardProps = {
  item: PlaylistItem;
  onHover?: () => void;
  progress?: number;
  posterAspect?: PosterAspectRatio;
  featured?: boolean;
  disabled?: boolean;
  loading?: boolean;
  isCurrent?: boolean;
  isLocked?: boolean;
  currentLabel?: string;
  url: string;
  headingLevel?: number;
  tabIndex?: number;
  railType?: string;
  showTitle?: string;
  showTag?: string;
};

function Card({
  onHover,
  progress,
  item,
  posterAspect = '16:9',
  featured = false,
  disabled = false,
  loading = false,
  isCurrent = false,
  isLocked = true,
  currentLabel,
  headingLevel = 3,
  url,
  tabIndex = 0,
  railType,
  showTitle = 'true',
  showTag,
}: CardProps): JSX.Element {
  const {
    title,
    duration,
    episodeNumber,
    seasonNumber,
    cardImage: image,
    mediaStatus,
    scheduledStart,
    'title-es': title_es,
    'title-en': title_en,
    contentType,
  } = item;
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'video']);
  // t('play_item')
  const { config } = useConfigStore.getState();
  const customParam = config?.custom;
  const { defaultLanguage, isThumbnailLocalized } = customParam ?? {};

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const cardClassName = classNames(styles.card, {
    [styles.featured]: featured,
    [styles.disabled]: disabled,
  });
  const LocalStorageValue = getLocalStorageValue();

  const aspectRatioClass = posterAspect ? styles[`aspect${posterAspect.replace(':', '') as PosterAspectRatioClass}`] : undefined;
  const posterClassNames = classNames(styles.poster, aspectRatioClass, {
    [styles.current]: isCurrent,
  });
  const posterImageClassNames = classNames(styles.posterImage, {
    [styles.visible]: imageLoaded,
  });

  const isSeriesItem = isSeries(item);
  const isLive = mediaStatus === MediaStatus.LIVE || isLiveChannel(item);
  const isScheduled = mediaStatus === MediaStatus.SCHEDULED;

  const renderTag = () => {
    if (loading || disabled || !title) return null;

    if (isSeriesItem) {
      return <div className={styles.tag}>{t('video:series')}</div>;
    } else if (episodeNumber) {
      return <div className={styles.tag}>{formatSeriesMetaString(seasonNumber, episodeNumber)}</div>;
    } else if (duration) {
      return <div className={styles.tag}>{formatDuration(duration)}</div>;
    } else if (isLive) {
      return <div className={classNames(styles.tag, styles.live)}>{t('live')}</div>;
    } else if (isScheduled) {
      return (
        <div className={styles.tag}>
          <Icon icon={Today} className={styles.scheduled} />
          {t('scheduled')}
        </div>
      );
    }
  };
  // commented out for future reference
  // useEffect(() => {
  //   if (railType) {
  //     setPortraitImg(`https://img.jwplayer.com/v1/media/${item.mediaid}/images/vertical.jpg`);
  //   }
  // }, [railType, item]);

  const checkImageExists = async (url: any) => {
    try {
      const response = await fetch(url);
      return response.ok;
    } catch (error) {
      console.error('Error checking image existence:', error);
      return false;
    }
  };

  useEffect(() => {
    const determineImageUrl = async () => {
      let selectedImageUrl = image;

      if (railType === 'vertical') {
        if (isThumbnailLocalized === 'true' && LocalStorageValue === 'en' && contentType === 'hub') {
          const enVerticalImageUrl = `https://img.jwplayer.com/v1/media/${item.mediaid}/images/vertical-en.jpg`;
          if (await checkImageExists(enVerticalImageUrl)) {
            selectedImageUrl = enVerticalImageUrl;
          } else {
            const verticalImageUrl = `https://img.jwplayer.com/v1/media/${item.mediaid}/images/vertical.jpg`;
            if (await checkImageExists(verticalImageUrl)) {
              selectedImageUrl = verticalImageUrl;
            } else {
              selectedImageUrl = image;
            }
          }
        } else {
          const verticalImageUrl = `https://img.jwplayer.com/v1/media/${item.mediaid}/images/vertical.jpg`;

          if (await checkImageExists(verticalImageUrl)) {
            selectedImageUrl = verticalImageUrl;
          } else {
            selectedImageUrl = image;
          }
        }
      } else if (isThumbnailLocalized === 'true' && LocalStorageValue === 'en' && contentType === 'hub') {
        const enHorizontalImageUrl = `https://img.jwplayer.com/v1/media/${item.mediaid}/images/horizontal-en.jpg`;
        if (await checkImageExists(enHorizontalImageUrl)) {
          selectedImageUrl = enHorizontalImageUrl;
        }
      } else {
        selectedImageUrl = image;
      }

      setImageUrl(selectedImageUrl);
    };

    determineImageUrl();
  }, [item, isThumbnailLocalized, LocalStorageValue, railType, contentType, image]);

  let selectedTitle;

  if (defaultLanguage === 'es') {
    if (LocalStorageValue === 'es' || LocalStorageValue === undefined) {
      selectedTitle = title;
    } else if (LocalStorageValue === 'en') {
      selectedTitle = title_en;
    }
  } else if (defaultLanguage === 'en') {
    if (LocalStorageValue === 'en' || LocalStorageValue === undefined) {
      selectedTitle = title;
    } else if (LocalStorageValue === 'es') {
      selectedTitle = title_es;
    }
  } else {
    selectedTitle = title;
  }
  const heading = React.createElement(
    `h${headingLevel}`,
    { className: classNames(styles.title, { [styles.loading]: loading }) },
    loading ? '' : selectedTitle ? selectedTitle : title,
  );

  const handleError = () => {
    setImageUrl(image);
  };

  return (
    <Link
      role="button"
      to={url}
      className={cardClassName}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      onMouseEnter={onHover}
      tabIndex={disabled ? -1 : tabIndex}
      data-testid={testId(title)}
    >
      {!featured && !disabled && (
        <div className={styles.titleContainer}>
          {showTitle === 'true' && heading}
          {!!scheduledStart && (
            <div className={classNames(styles.scheduledStart, { [styles.loading]: loading })}>{formatLocalizedDateTime(scheduledStart, language)}</div>
          )}
        </div>
      )}
      <div className={posterClassNames}>
        {railType && imageUrl ? (
          <Image
            className={posterImageClassNames}
            image={imageUrl}
            width={featured ? 640 : 320}
            onLoad={() => setImageLoaded(true)}
            alt="img"
            handleError={handleError}
          />
        ) : (
          <Image className={posterImageClassNames} image={imageUrl} width={featured ? 640 : 320} onLoad={() => setImageLoaded(true)} alt="" />
        )}
        {!loading && (
          <div className={styles.meta}>
            <h2 className={styles.title}>{featured && !disabled && showTitle === 'true' && heading}</h2>
            <div className={styles.tags}>
              {isLocked && (
                <div className={classNames(styles.tag, styles.lock)} aria-label={t('card_lock')} role="img">
                  <Icon icon={Lock} />
                </div>
              )}
              {showTag === 'true' && renderTag()}
            </div>
          </div>
        )}
        {isCurrent && <div className={styles.currentLabel}>{currentLabel}</div>}
        {progress ? (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar} style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export default memo(Card);
